import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tooltip,
  Link,
} from "@mui/material";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridRowParams,
} from "@mui/x-data-grid";

interface Row {
  id: number;
  name: string;
  live: boolean;
  slug: string;
  fsc_score?: string;
  fsc_v2_score?: string;
  ipi_score?: string;
  timber_risk_score?: string;
  cpi_score?: string;
}

const CountriesInSpecies = ({ props }: any) => {
  const [rows, setRows] = useState<Row[]>([]);
  const navigate = useNavigate();

  const CustomToolTip = (name: string, tooltipString: string) => {
    return (
      <Tooltip title={tooltipString} placement="top">
        <div className="MuiDataGrid-columnHeaderTitle css-1jbbcbn-MuiDataGrid-columnHeaderTitle">
          {name}
        </div>
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "name",
      headerName: "Country",
      editable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridCellParams) => (
        <Tooltip
          title={
            params.row.live
              ? "Click to View Country Details"
              : "No Country Information Tracked"
          }
          placement="right"
        >
          <Box className="csutable-cell-trucate" sx={{ textAlign: "center" }}>
            {params.row.name}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "ipi_score",
      //headerName: "Index of Public Integrity (IPI) Score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Index of Public Integrity (IPI) Score",
          "Standardized measure of six measures for public integrity and transparency. Updated annually.  Source: CorruptionRisk.org."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "timber_risk_score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Timber Sourcing Hub Risk Score",
          "Comprehensive assessment of timber illegality associated with specific countries. Source: Preferred by Nature Timber Sourcing Hub."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "cpi_score",
      headerName: "Corruption Perceptions Index (CPI) Score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Corruption Perceptions Index (CPI) Score",
          "Measure of perceived government corruption as determined by a combination of surveys and independent assessment.  Updated annually. Source: Transparency International"
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fsc_score",
      headerName: "FSC v1 NRA Specified Risk Indicators",
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip
          title={
            <React.Fragment>
              The number of 32 discreet indicators evaluated by FSC National
              Risk Assessments that is determined to have elevated risk
              associated with a specific attribute. Mitigation measures are
              identified for mitigating Specified Risk. Sources: FSC
              International Document Center and FSC Risk Assessment Platform.
              For more information, see the{" "}
              <Link
                href="/understand-risk"
                target="_blank"
                style={{ color: "#6db2b8" }}
              >
                Understanding Risk section
              </Link>
              .{" "}
            </React.Fragment>
          }
          placement="top"
        >
          <div className="MuiDataGrid-columnHeaderTitle css-1jbbcbn-MuiDataGrid-columnHeaderTitle">
            FSC v1 NRA Specified Risk Indicators
          </div>
        </Tooltip>
      ),
      editable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridCellParams) => (
        <Box className="csutable-cell-trucate" sx={{ textAlign: "center" }}>
          {params.row.fsc_score}
        </Box>
      ),
    },
    {
      field: "fsc_v2_score",
      headerName: "FSC v2 NRA Non-negligible Risk Indicators",
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip
          title={
            <React.Fragment>
              The number of 64 discreet indicators evaluated by FSC National
              Risk Assessments that is determined to have elevated risk
              associated with a specific attribute. Mitigation measures are
              identified for mitigating Non-negligible Risk. Sources: FSC
              International Document Center and FSC Risk Assessment Platform.
              For more information, see the{" "}
              <Link
                href="/understand-risk"
                target="_blank"
                style={{ color: "#6db2b8" }}
              >
                Understanding Risk section
              </Link>
              .{" "}
            </React.Fragment>
          }
          placement="top"
        >
          <div className="MuiDataGrid-columnHeaderTitle css-1jbbcbn-MuiDataGrid-columnHeaderTitle">
            FSC v2 NRA Non-negligible Risk Indicators
          </div>
        </Tooltip>
      ),
      editable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridCellParams) => (
        <Box className="csutable-cell-trucate" sx={{ textAlign: "center" }}>
          {params.row.fsc_v2_score}
        </Box>
      ),
    },
  ];

  const trackedCountry = (e: any) => {
    if (e.live) {
      navigate(`/assess-risk/countries/${e.slug}`, {
        state: { id: e.id, name: e.name, slug: e.slug },
      });
    }
  };

  useEffect(() => {
    setRows([]);

    props.map((country: any) => {
      console.log(country);

      let entryRow = {
        id: country.id,
        name: country.name,
        live: country.live,
        slug: country.meta.slug,
        fsc_score: country?.fsc_total_risk,
        fsc_v2_score: country?.fsc_v2_total_risk,
        ipi_score: country?.ipi_score,
        cpi_score: country?.cpi_score,
        timber_risk_score: country?.timber_risk_score,
      };
      setRows((rows) => [...rows, entryRow]);
    });
  }, []);

  return (
    <DataGrid
      className="countriesInSpecies"
      initialState={{
        columns: {
          columnVisibilityModel: {
            id: false,
          },
        },
      }}
      sx={{
        marginTop: "10px",
        fontSize: "17px",
        width: "100%",
        height: "400px",
        "& .MuiDataGrid-columnHeaderTitle": {
          textOverflow: "clip",
          whiteSpace: "break-spaces",
          lineHeight: 1.2,
        },
      }}
      headerHeight={110}
      rows={rows}
      columns={columns}
      disableColumnFilter
      disableColumnMenu
      onRowClick={(params: GridRowParams<any>) => {
        console.log(params.row.live);
        if (params.row.live) {
          trackedCountry(params.row);
        }
      }}
      isRowSelectable={(params: GridRowParams<any>) => params.row.live}
      hideFooter
      //autoHeight
    />
  );
};

export default CountriesInSpecies;
